<template>
  <div class="task-details-info">
    <div class="task-details-info__main">
      <div class="task-details-info__fields">
        <div
          v-for="(row, rowIndex) in fieldRows"
          :key="row.title || rowIndex"
          class="task-details-info__group"
        >
          <div
            v-if="row.title"
            class="task-details-info__group-title"
          >
            {{ row.title }}
          </div>

          <fields-renderer
            :config="{ columnsCount: row.columnsCount, fields: row.fields }"
            :form-data="formData"
            :fields-config="fieldsConfig"
            :validation-errors="validationErrors"
            :initial-autocompletes="initialAutocompletes"
            :permissions="permissions"
            :edit="edit"
            :create-mode="createMode"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
            @unlock-fields-on-dbl-click="unlockFieldsOnDblClick"
          />
        </div>
      </div>

      <!--    A fixed height, whatever the form beside it comes to - see the styles
              below.-->
      <div class="task-details-info__side">
        <div class="task-details-info__side-inner">
          <task-paths-map-panel
            :streets="streets"
          />
        </div>
      </div>
    </div>

    <!--    A task being created has no history to show yet - the box is what the record
            it is about to become will be entered into.-->
    <winter-service-log-history
      v-if="!createMode"
      :record="formData"
      :translation-namespace="NS"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
import TaskPathsMapPanel from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskPathsMapPanel.vue'
import WinterServiceLogHistory from '@/modules/road-maintenance-module/winter-service-v2/common/components/WinterServiceLogHistory.vue'
import { useTaskStreetPaths } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskStreetPaths'
import { TASKS_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its `rows` hold the grouped
  // field lists (see useTaskDetails for why they live there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits(['set-edit'])

const { loadStreets, streetsChangedAt } = useTaskStreetPaths()

const streets = ref([])

const fieldRows = computed(() => props.config?.rows || [])

// FieldsRenderer only reports the double-click; turning the tab into edit mode is the
// block's job, and Content.vue is what listens for `set-edit`. It has already checked
// itself - what is left is the tab-level guard.
const unlockFieldsOnDblClick = () => {
  if (props.createMode || (props.permissions && !props.permissions.canEdit)) {
    return
  }

  emit('set-edit', true)
}

// The task endpoint reports no geometry, so the route drawn beside the form has to be
// fetched from the task's own streets. A task being created has none yet.
// The colour the streets currently on the map were fetched with. The form edits its
// own copy of the task as it is typed in, so this is the only way to tell what the map
// is actually showing from what the user has picked but not saved yet.
let drawnColor = null

const loadTaskStreets = async ({ reload = false } = {}) => {
  const taskId = props.formData?.id

  if (props.createMode || !taskId) {
    streets.value = []
    return
  }

  streets.value = await loadStreets([taskId], { reload })
  drawnColor = props.formData?.color ?? null
}

// One watcher rather than a mount hook beside it: the record usually arrives just
// after the tab is mounted, and the two together asked for the same streets twice.
watch(() => props.formData?.id, () => loadTaskStreets(), { immediate: true })

// The route tab writes streets through its own endpoints, so a street added, changed
// or deleted there has to reach this map too. What it dropped is already gone from
// what is remembered, so this only has to ask for it again.
watch(streetsChangedAt, () => loadTaskStreets())

// The colour a street is drawn in belongs to the task, so a saved colour has to reach
// the map - the streets already fetched still carry the old one. Driven by the save
// itself rather than by the colour: `updated_at` only moves when the task is written,
// so picking colours in the form leaves the map alone until one is actually saved.
watch(() => props.formData?.updated_at, (updatedAt, previousUpdatedAt) => {
  // The record arriving is not a save: at that point the streets are still on their
  // way, so a reload here would only fire the very same request a second time.
  if (previousUpdatedAt === undefined || updatedAt === previousUpdatedAt) {
    return
  }

  if ((props.formData?.color ?? null) === drawnColor) {
    return
  }

  loadTaskStreets({ reload: true })
})
</script>

<style scoped>
.task-details-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
}

.task-details-info__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  /* The map keeps its own height, so the row must not stretch its cell to the
     form's - see the side column below. */
  align-items: start;
}

.task-details-info__fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.task-details-info__group {
  padding: 12px 20px 4px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

.task-details-info__group-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

/* The map is a fixed height on both screens it appears on - the details tab and the
   new task screen - rather than however tall the form beside it happens to be: the
   two carry different field counts, and a map that follows them is a different map on
   each. */
.task-details-info__side {
  min-width: 0;
  height: 480px;
}

.task-details-info__side-inner {
  height: 100%;
}

/* Stacked under the form, and shorter: the map has the full width to itself here. */
@media (max-width: 960px) {
  .task-details-info__main {
    grid-template-columns: minmax(0, 1fr);
  }

  .task-details-info__side {
    height: 320px;
  }
}
</style>
